import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { sectionCode, useNav } from "./api";
import styles from "./manual.module.css";
import { hasReadablePage } from "./navTree";

import wordmarkUrl from "@/assets/wordmark.png";
import { BetaNote } from "@/components/BetaBadge";
import { ClockIcon, DocumentIcon } from "@/components/icons";
import { PageHeader } from "@/components/PageHeader";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { httpClient } from "@/lib/httpClient";

interface RecentPageEntry {
  pageId: string;
  sectionSlug: string;
  sectionTitle: string;
  pageSlug: string;
  title: string;
  publishedAt: string;
}

interface LastRead {
  path: string;
  title: string;
  sectionTitle: string;
  at: number;
}

function readLastRead(): LastRead | null {
  try {
    const raw = localStorage.getItem("opshub:last-read");
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LastRead;
    return parsed.path && parsed.title ? parsed : null;
  } catch {
    return null;
  }
}

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short" });
}

/** Hub home: what needs your attention, what changed, and the section directory. */
export function ManualHome() {
  const { sections, error } = useNav();
  const { permissions } = useCurrentUser();
  const [recent, setRecent] = useState<RecentPageEntry[] | null>(null);
  const [pendingCount, setPendingCount] = useState<number | null>(null);
  const lastRead = readLastRead();

  useEffect(() => {
    document.title = "Birzulis OpsHub";
  }, []);

  useEffect(() => {
    let cancelled = false;
    httpClient
      .get<{ pages: RecentPageEntry[] }>("/api/cms/recent?limit=5")
      .then((r) => {
        if (!cancelled) setRecent(r.pages);
      })
      .catch(() => {
        if (!cancelled) setRecent([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Directors: surface the approvals queue right on home.
  const canApprove = permissions.includes("cms.approve");
  useEffect(() => {
    if (!canApprove) return;
    let cancelled = false;
    httpClient
      .get<{ pending: unknown[] }>("/api/cms/approvals")
      .then((r) => {
        if (!cancelled) setPendingCount(r.pending.length);
      })
      .catch(() => {
        if (!cancelled) setPendingCount(null);
      });
    return () => {
      cancelled = true;
    };
  }, [canApprove]);

  return (
    <div className={styles.view}>
      <PageHeader
        title={<img src={wordmarkUrl} alt="Birzulis OpsHub" className={styles.homeWordmark} />}
        intro="Every process, in one place. Browse OpsHub by section, or search for the procedure you need."
      />
      <BetaNote />
      {error && (
        <div className={styles.emptyState}>
          OpsHub couldn&rsquo;t be loaded. Refresh the page, or check with your manager if this
          keeps happening.
        </div>
      )}
      {!sections && !error && <div className={styles.quiet}>Loading…</div>}

      {pendingCount !== null && pendingCount > 0 && (
        <Link to="/approvals" className={styles.approvalsBanner}>
          <DocumentIcon />
          <strong>
            {pendingCount} page{pendingCount === 1 ? "" : "s"} waiting for your approval
          </strong>
          <span className={styles.approvalsBannerGo}>Review →</span>
        </Link>
      )}

      {(lastRead || (recent && recent.length > 0)) && (
        <div className={styles.activityRow}>
          {lastRead && (
            <Link to={lastRead.path} className={styles.activityCard}>
              <div className={styles.activityLabel}>Continue reading</div>
              <div className={styles.activityTitle}>{lastRead.title}</div>
              <div className={styles.activityMeta}>{lastRead.sectionTitle}</div>
            </Link>
          )}
          {recent && recent.length > 0 && (
            <div className={styles.activityCard}>
              <div className={styles.activityLabel}>
                <ClockIcon /> Recently updated
              </div>
              <ul className={styles.recentList}>
                {recent.map((p) => (
                  <li key={p.pageId}>
                    <Link to={`/manual/${p.sectionSlug}/${p.pageSlug}`}>
                      {p.title}
                      <span className={styles.recentWhen}>{formatWhen(p.publishedAt)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className={styles.homeGrid}>
        {/* A section with no published page the reader can read is left out;
            section codes stay positional, so the others keep theirs. */}
        {(sections ?? []).filter(hasReadablePage).map((section) => {
          const count = section.pages.length;
          const code = sectionCode(section.ordinal);
          return (
            <Link key={section.id} to={`/manual/${section.slug}`} className={styles.card}>
              <div className={styles.cardTitle}>
                <span className={styles.cardCode}>{code}</span>
                {section.title}
              </div>
              <div className={styles.cardMeta}>
                {count} page{count === 1 ? "" : "s"}
                {section.restricted ? " · restricted" : ""}
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
